import React, { useState, useRef, useEffect } from 'react';
import { GeneratedImage } from '@/types';
import { ChevronDown, Plus, X, RotateCw, Trash2, Download, FolderOpen, MoreHorizontal } from 'lucide-react';
import { useApp } from '@/store/AppContext';
import { useNavigate } from 'react-router-dom';
import { getPresetNameSafe } from '@/utils/presetHelpers';
import { useTranslation, getPeopleTranslationKey, getSceneTypeTranslationKey, getLocationTranslationKey, formatDate } from '@/i18n/useTranslation';
import { buildDownloadFilename } from '@/utils/downloadFilename';
import apiClient from '@/api/apiClient';

interface InfoPanelProps {
  image: GeneratedImage | null;
  isVisible: boolean;
  onToggle: () => void;
  onImageUpdate?: (imageId: string, updates: Partial<GeneratedImage>) => void;
  onRegenerate?: (image: GeneratedImage) => void;
  onDelete?: (image: GeneratedImage) => void;
}

const PREDEFINED_TAGS = ['Rejected', 'Needs Review', 'Approved'];

export default function InfoPanel({ image, isVisible, onToggle, onImageUpdate, onRegenerate, onDelete }: InfoPanelProps) {
  const navigate = useNavigate();
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [editingTags, setEditingTags] = useState(false);
  const [tags, setTags] = useState<string[]>(image?.params?.images_tags || []);
  const [customTag, setCustomTag] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [savingTags, setSavingTags] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [fileSizes, setFileSizes] = useState<{ jpg: string; png: string }>({ jpg: '...', png: '...' });
  const downloadRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const { presets } = useApp();
  const { t } = useTranslation();

  if (!image) return null;

  const sessionId = image.params?.designSessionId || image.params?.design_session || image.params?.sessionId || null;
  const imageStyleForSession = image.params?.imageStyle || (image.params?.sceneType === 'mockup' ? 'mockup' : 'photo');

  const referencePanelImages = [
    image.params?.productImageUrl
      ? { src: image.params.productImageUrl, alt: t('label.referenceProductImage') }
      : null,
    image.params?.referenceImageUrl
      ? {
          src: image.params.referenceImageUrl,
          alt: image.params?.imageStyle === 'infographic'
            ? t('infoPanel.styleReference')
            : t('referenceImage.title'),
        }
      : null,
    image.params?.contentImageUrl
      ? { src: image.params.contentImageUrl, alt: t('label.contentReference') }
      : null,
  ].filter((item): item is { src: string; alt: string } => Boolean(item));

  const referencePanelTitle =
    referencePanelImages.length === 1
      ? referencePanelImages[0].alt
      : t('referenceImage.title');

  const handleAddTag = (tag: string) => {
    if (!tags.includes(tag)) {
      setTags([...tags, tag]);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const handleAddCustomTag = () => {
    if (customTag.trim() && !tags.includes(customTag.trim())) {
      setTags([...tags, customTag.trim()]);
      setCustomTag('');
      setShowCustomInput(false);
    }
  };

  const handleSaveTags = async () => {
    if (!image?.id) return;
    
    setSavingTags(true);
    try {
      const response = await apiClient.updateImageTags(image.id, tags);
      if (response.success) {
        // Update local image state
        if (onImageUpdate) {
          onImageUpdate(image.id, {
            params: {
              ...image.params,
              images_tags: tags
            }
          });
        }
        setEditingTags(false);
      } else {
        alert('Failed to save tags: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to save tags:', error);
      alert('Failed to save tags');
    } finally {
      setSavingTags(false);
    }
  };

  const handleCancelEdit = () => {
    setTags(image?.params?.images_tags || []);
    setEditingTags(false);
    setShowCustomInput(false);
    setCustomTag('');
  };

  // Close download dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target as Node)) {
        setDownloadOpen(false);
      }
    };
    if (downloadOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [downloadOpen]);

  // Close actions dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target as Node)) {
        setActionsOpen(false);
      }
    };
    if (actionsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [actionsOpen]);

  // Fetch file sizes when dropdown opens
  useEffect(() => {
    if (!downloadOpen || !image) return;
    const fetchFileSizes = async () => {
      try {
        const formats = ['jpg', 'png'] as const;
        const sizes: { jpg: string; png: string } = { jpg: '...', png: '...' };
        await Promise.all(
          formats.map(async (format) => {
            try {
              let url = image.url;
              if (image.url.includes('/assets/')) {
                const baseUrl = image.url.replace(/\.(jpg|jpeg|png)$/i, '');
                url = `${baseUrl}?format=${format}`;
              }
              const response = await fetch(url, { method: 'HEAD' });
              const contentLength = response.headers.get('content-length');
              if (contentLength) {
                const sizeInMB = (parseInt(contentLength) / (1024 * 1024)).toFixed(2);
                sizes[format] = `${sizeInMB} MB`;
              }
            } catch {}
          })
        );
        setFileSizes(sizes);
      } catch {}
    };
    fetchFileSizes();
  }, [downloadOpen, image]);

  const handleDownload = async (format: 'jpg' | 'png') => {
    if (!image) return;
    setDownloading(true);
    setDownloadOpen(false);
    try {
      let downloadUrl = image.url;
      if (image.url.includes('/assets/')) {
        const baseUrl = image.url.replace(/\.(jpg|jpeg|png)$/i, '');
        downloadUrl = `${baseUrl}?format=${format}&quality=100&download`;
      }
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = buildDownloadFilename(image.filename, format);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download image. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // Hide Camera Settings on localhost only
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

  // Get camera settings from presets
  const presetsData = image.params?.presets || {};
  const getPresetValue = (path: string, value: string | undefined) => {
    if (!value) return '-';
    return getPresetNameSafe(path, value, presets);
  };

  // Format exposure (aperture / shutter speed)
  const aperture = presetsData.aperture ? getPresetValue('cameraSettings.aperture', presetsData.aperture) : '-';
  const shutterSpeed = presetsData.shutterSpeed ? getPresetValue('cameraSettings.shutterSpeed', presetsData.shutterSpeed) : '-';
  const exposureText = aperture !== '-' && shutterSpeed !== '-' 
    ? `${aperture} / ${shutterSpeed}` 
    : (aperture !== '-' ? aperture : (shutterSpeed !== '-' ? shutterSpeed : '-'));

  // Get other camera settings
  const focalLength = presetsData.lens ? getPresetValue('other.lens', presetsData.lens) : '-';
  const iso = presetsData.iso ? getPresetValue('cameraSettings.iso', presetsData.iso) : '-';
  const whiteBalance = presetsData.whiteBalance ? getPresetValue('cameraSettings.whiteBalance', presetsData.whiteBalance) : '-';
  const focusMode = presetsData.focusMode ? getPresetValue('cameraSettings.focusMode', presetsData.focusMode) : '-';
  const meteringMode = presetsData.meteringMode ? getPresetValue('cameraSettings.meteringMode', presetsData.meteringMode) : '-';

  // Check if Camera Settings section has any data
  const hasCameraSettings = exposureText !== '-' || focalLength !== '-' || iso !== '-' || whiteBalance !== '-' || focusMode !== '-' || meteringMode !== '-';
  
  // Check if Scene & Location section has any data
  const hasSceneLocation = image.params?.sceneType || image.params?.location || image.params?.people;

  return (
    <>
      {/* Right Sidebar - hidden on mobile */}
      <div 
        className="right-sidebar hidden md:block w-[300px] bg-bg-panel border-l border-border overflow-y-scroll flex-shrink-0 fixed top-0 bottom-0 z-50 shadow-[-2px_0_8px_rgba(0,0,0,0.1)]"
      style={{
          right: isVisible ? '0' : '-300px',
          transition: 'right 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <div className="p-4">
          {/* File Info Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] font-semibold text-text-secondary">{t('infoPanel.fileInfo')}</div>
              {onDelete && (
                <div className="relative" ref={actionsRef}>
                  <button
                    type="button"
                    onClick={() => setActionsOpen((prev) => !prev)}
                    className="h-7 w-7 rounded-md text-text-secondary hover:text-text-primary hover:bg-bg-control transition-all flex items-center justify-center"
                    title={t('button.edit')}
                  >
                    <MoreHorizontal className="w-4 h-4" strokeWidth={2} />
                  </button>
                  {actionsOpen && (
                    <div className="absolute top-full right-0 mt-1 min-w-[120px] bg-bg-panel border border-border rounded-md shadow-xl overflow-hidden z-[100]">
                      <button
                        type="button"
                        onClick={() => {
                          setActionsOpen(false);
                          onDelete(image);
                        }}
                        className="w-full px-3 py-2 text-sm text-red-400 hover:bg-bg-control text-left transition-colors flex items-center gap-2"
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                        <span>{t('button.delete')}</span>
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-[11px] text-text-secondary">{t('infoPanel.created')}</span>
                <span className="text-[11px] text-text-primary">{formatDate(image.timestamp)}</span>
              </div>

              {sessionId && (
                <div className="pt-1">
                  <button
                    onClick={() => navigate(`/create/${sessionId}?imageStyle=${encodeURIComponent(imageStyleForSession)}`)}
                    className="btn btn-secondary btn-sm btn-block"
                  >
                    <FolderOpen className="w-3.5 h-3.5" strokeWidth={2} />
                    <span>{t('button.openFolder')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Scene & Location – top of details, right after file info */}
          {hasSceneLocation && image.params?.imageStyle !== 'infographic' && (
            <div className="mb-6">
              <div className="text-[11px] font-semibold text-text-secondary mb-3">{t('infoPanel.sceneLocation')}</div>
              
              <div className="space-y-2">
                {image.params.sceneType && (
                  <div className="flex justify-between">
                    <span className="text-[11px] text-text-secondary">{t('infoPanel.sceneType')}</span>
                    <span className="text-[11px] text-text-primary">{t(getSceneTypeTranslationKey(image.params.sceneType))}</span>
                  </div>
                )}
                
                {image.params.location && (
                  <div className="flex justify-between">
                    <span className="text-[11px] text-text-secondary">{t('infoPanel.location')}</span>
                    <span className="text-[11px] text-text-primary">{t(getLocationTranslationKey(image.params.location))}</span>
                  </div>
                )}
                
                {image.params.people && (
                  <div className="flex justify-between">
                    <span className="text-[11px] text-text-secondary">{t('infoPanel.people')}</span>
                    <span className="text-[11px] text-text-primary">{t(getPeopleTranslationKey(image.params.people))}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reference images – product, style/content refs in 2-column grid */}
          {referencePanelImages.length > 0 && (
            <div className="mb-6">
              <div className="text-[11px] font-semibold text-text-secondary mb-3">{referencePanelTitle}</div>
              <div className="grid grid-cols-2 gap-2">
                {referencePanelImages.map((ref) => (
                  <div key={ref.src} className="rounded overflow-hidden border border-border bg-bg-control aspect-square">
                    <img
                      src={ref.src}
                      alt={ref.alt}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        const img = e.target as HTMLImageElement;
                        img.style.display = 'none';
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Camera Settings Section - hidden from users */}
          {false && hasCameraSettings && !isLocalhost && (
            <div className="mb-6">
              <div className="text-[11px] font-semibold text-text-secondary mb-3">{t('infoPanel.cameraSettings')}</div>
              
              <div className="space-y-2">
                {exposureText !== '-' && (
                  <div className="flex justify-between">
                    <span className="text-[11px] text-text-secondary">{t('infoPanel.exposure')}</span>
                    <span className="text-[11px] text-text-primary">{exposureText}</span>
                  </div>
                )}
                
                {focalLength !== '-' && (
                  <div className="flex justify-between">
                    <span className="text-[11px] text-text-secondary">{t('infoPanel.focalLength')}</span>
                    <span className="text-[11px] text-text-primary">{focalLength}</span>
                  </div>
                )}
                
                {iso !== '-' && (
                  <div className="flex justify-between">
                    <span className="text-[11px] text-text-secondary">{t('infoPanel.iso')}</span>
                    <span className="text-[11px] text-text-primary">{iso}</span>
                  </div>
                )}
                
                {whiteBalance !== '-' && (
                  <div className="flex justify-between">
                    <span className="text-[11px] text-text-secondary">{t('infoPanel.whiteBalance')}</span>
                    <span className="text-[11px] text-text-primary">{whiteBalance}</span>
                  </div>
                )}
                
                {focusMode !== '-' && (
                  <div className="flex justify-between">
                    <span className="text-[11px] text-text-secondary">{t('infoPanel.focus')}</span>
                    <span className="text-[11px] text-text-primary">{focusMode}</span>
                  </div>
                )}
                
                {meteringMode !== '-' && (
                  <div className="flex justify-between">
                    <span className="text-[11px] text-text-secondary">{t('infoPanel.metering')}</span>
                    <span className="text-[11px] text-text-primary">{meteringMode}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tags Section - HIDDEN FOR NOW */}
          {false && !hasSceneLocation && (
            <div className="mb-6">
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="text-[11px] font-semibold text-text-secondary">{t('infoPanel.tags')}</div>
                  {!editingTags ? (
                    <button
                      onClick={() => setEditingTags(true)}
                      className="text-[10px] px-2 py-1 bg-accent hover:bg-accent-hover text-white rounded transition-colors"
                    >
                      Edit
                    </button>
                  ) : (
                    <div className="flex gap-1">
                      <button
                        onClick={handleSaveTags}
                        disabled={savingTags}
                        className="text-[10px] px-2 py-1 bg-green-600 hover:bg-green-700 text-white rounded transition-colors disabled:opacity-50"
                      >
                        {savingTags ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        disabled={savingTags}
                        className="text-[10px] px-2 py-1 bg-bg-control hover:bg-bg-control-hover border border-border text-text-primary rounded transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
                
                {editingTags ? (
                  <div className="space-y-2">
                    {/* Current tags */}
                    <div className="flex flex-wrap gap-1">
                      {tags.map((tag, idx) => (
                        <span key={idx} className="text-[10px] px-1.5 py-0.5 bg-bg-control border border-border rounded text-text-primary flex items-center gap-1">
                          {tag}
                          <button
                            onClick={() => handleRemoveTag(tag)}
                            className="hover:text-red-500"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      ))}
                      {tags.length === 0 && (
                        <span className="text-[11px] text-text-disabled">No tags</span>
                      )}
                    </div>
                    
                    {/* Add predefined tags */}
                    <div className="space-y-1">
                      <div className="text-[10px] text-text-secondary">Add tag:</div>
                      <div className="flex flex-wrap gap-1">
                        {PREDEFINED_TAGS.map(tag => (
                          <button
                            key={tag}
                            onClick={() => handleAddTag(tag)}
                            disabled={tags.includes(tag)}
                            className="text-[10px] px-2 py-1 bg-bg-control hover:bg-bg-control-hover border border-border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {tag}
                          </button>
                        ))}
                        <button
                          onClick={() => setShowCustomInput(!showCustomInput)}
                          className="text-[10px] px-2 py-1 bg-accent hover:bg-accent-hover text-white border border-accent rounded transition-colors flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" />
                          Custom
                        </button>
                      </div>
                    </div>
                    
                    {/* Custom tag input */}
                    {showCustomInput && (
                      <div className="flex gap-1">
                        <input
                          type="text"
                          value={customTag}
                          onChange={(e) => setCustomTag(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddCustomTag()}
                          placeholder="Enter custom tag"
                          className="flex-1 text-[11px] px-2 py-1 bg-bg-control border border-border rounded text-text-primary focus:outline-none focus:border-accent"
                        />
                        <button
                          onClick={handleAddCustomTag}
                          className="text-[10px] px-2 py-1 bg-accent hover:bg-accent-hover text-white rounded transition-colors"
                        >
                          Add
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {tags.length > 0 ? (
                      tags.map((tag, idx) => (
                        <span key={idx} className="text-[10px] px-1.5 py-0.5 bg-bg-control border border-border rounded text-text-primary">
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="text-[11px] text-text-disabled">No tags</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons Section - at bottom */}
          <div className="mt-6 pt-6 border-t border-border space-y-2">
            {/* Download Button with dropdown */}
            <div className="relative w-full" ref={downloadRef}>
              <button
                onClick={() => setDownloadOpen(!downloadOpen)}
                disabled={downloading}
                className="btn btn-secondary btn-md btn-block rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloading ? (
                  <div className="w-4 h-4 border-2 border-text-primary border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <Download className="w-4 h-4" strokeWidth={2} />
                    <span>{t('button.download')}</span>
                  </>
                )}
              </button>
              
              {/* Dropdown Menu */}
              {downloadOpen && !downloading && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-bg-panel border border-border rounded-lg shadow-xl overflow-hidden z-[9999]">
                  <button
                    onClick={() => handleDownload('jpg')}
                    className="w-full px-4 py-2.5 text-left text-sm text-text-primary hover:bg-bg-control transition-colors flex items-center justify-between"
                  >
                    <span className="font-medium">JPG</span>
                    <span className="text-xs text-text-secondary">{fileSizes.jpg}</span>
                  </button>
                  <button
                    onClick={() => handleDownload('png')}
                    className="w-full px-4 py-2.5 text-left text-sm text-text-primary hover:bg-bg-control transition-colors flex items-center justify-between border-t border-border"
                  >
                    <span className="font-medium">PNG</span>
                    <span className="text-xs text-text-secondary">{fileSizes.png}</span>
                  </button>
                </div>
              )}
            </div>
            
            {/* Use as Reference Button - same style as download */}
            {onRegenerate && (
              <button
                onClick={() => onRegenerate(image)}
                className="btn btn-secondary btn-md btn-block rounded-lg"
              >
                <RotateCw className="w-4 h-4" strokeWidth={2} />
                Use as Reference
              </button>
            )}
            
          </div>

        </div>
      </div>
    </>
  );
}
